import Add from '@mui/icons-material/Add';
import { paperClasses, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FuelSurchargeScheduleType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  LoadingSpinner,
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { FuelSurchargeSchedule } from '~hooks/useFuelSurchargeSchedule/model';
import { useFuelSurchargeSchedule } from '~hooks/useFuelSurchargeSchedule/useFuelSurchargeSchedule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { ItemNameAndId } from '~types/ItemNameAndId';
import fetchAllPages from '~utils/fetchAllPages';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  FuelSurchargeScheduleForm,
  FuelSurchargeScheduleFormHandler,
} from './components/FuelSurchargeScheduleForm';
import { FuelSurchargeSchedulePricesTable } from './components/FuelSurchargeSchedulePricesTable';
import { FuelSurchargeScheduleRow } from './components/FuelSurchargeScheduleRow';

interface FuelSurchargeScheduleDialogState {
  isOpen: boolean;
  isSaving: boolean;
  mode: 'create' | 'edit';
  editingSchedule?: FuelSurchargeSchedule;
}

interface DeleteDialogState {
  isOpen: boolean;
  isDeleting: boolean;
  scheduleToDelete?: FuelSurchargeSchedule;
}

const FuelSurchargeSchedules = observer(function FuelSurchargeSchedules() {
  const theme = useTheme();
  const { toasterStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const {
    getCompanyFuelSurchargeSchedules,
    createFuelSurchargeSchedule,
    updateFuelSurchargeSchedule,
    deleteFuelSurchargeSchedule,
    isLoading,
  } = useFuelSurchargeSchedule();
  const [schedules, setSchedules] = useState<FuelSurchargeSchedule[]>([]);
  const [selectedSchedule, setSelectedSchedule] = useState<
    FuelSurchargeSchedule | undefined
  >();
  const formRef = useRef<FuelSurchargeScheduleFormHandler>(null);

  const [dialogState, setDialogState] = useState<FuelSurchargeScheduleDialogState>({
    isOpen: false,
    isSaving: false,
    mode: 'create',
  });

  const [deleteDialogState, setDeleteDialogState] = useState<DeleteDialogState>({
    isOpen: false,
    isDeleting: false,
  });

  const loadSchedules = async () => {
    try {
      const allData = await fetchAllPages((cursor, limit) =>
        getCompanyFuelSurchargeSchedules({ cursor, limit }),
      );
      setSchedules(allData);
    } catch (error) {
      console.error('Failed to load fuel surcharge schedules:', error);
      setSchedules([]);
    }
  };

  useEffect(() => {
    setSelectedSchedule(undefined);
    loadSchedules();
  }, [companyId]);

  const openCreateDialog = () => {
    setDialogState({
      isOpen: true,
      isSaving: false,
      mode: 'create',
    });
  };

  const openEditDialog = (schedule: FuelSurchargeSchedule) => {
    setDialogState({
      isOpen: true,
      isSaving: false,
      mode: 'edit',
      editingSchedule: schedule,
    });
  };

  const closeDialog = () => {
    setDialogState((state) => ({ ...state, isOpen: false }));
  };

  const handleCreateSchedule = async () => {
    const formData = await formRef.current?.submit();
    if (!formData) {
      return;
    }

    try {
      setDialogState((state) => ({ ...state, isSaving: true }));

      const newSchedule = await createFuelSurchargeSchedule({
        name: formData.name,
        fuelPriceIndexId: formData.fuelPriceIndex.id,
        surchargeType: formData.surchargeType,
      });

      setSchedules((prev) => [...prev, newSchedule]);

      toasterStore.push(
        alert(
          t('administration.fuel_surcharge_schedule.created_message'),
          AlertTypes.success,
        ),
      );

      closeDialog();
    } catch (error) {
      console.error('Failed to create fuel surcharge schedule:', error);
    } finally {
      setDialogState((state) => ({
        ...state,
        isSaving: false,
      }));
    }
  };

  const handleUpdateSchedule = async () => {
    const formData = await formRef.current?.submit();
    if (!formData || !dialogState.editingSchedule) {
      return;
    }

    try {
      setDialogState((state) => ({ ...state, isSaving: true }));

      const updatedSchedule = await updateFuelSurchargeSchedule({
        id: dialogState.editingSchedule.id,
        name: formData.name,
        fuelPriceIndexId: formData.fuelPriceIndex.id,
        surchargeType: formData.surchargeType,
      });

      setSchedules((prev) =>
        prev.map((schedule) =>
          schedule.id === updatedSchedule.id ? updatedSchedule : schedule,
        ),
      );

      if (selectedSchedule?.id === updatedSchedule.id) {
        setSelectedSchedule(updatedSchedule);
      }

      toasterStore.push(
        alert(
          t('administration.fuel_surcharge_schedule.updated_message'),
          AlertTypes.success,
        ),
      );

      closeDialog();
    } catch (error) {
      console.error('Failed to update fuel surcharge schedule:', error);
    } finally {
      setDialogState((state) => ({
        ...state,
        isSaving: false,
      }));
    }
  };

  const handleEdit = (schedule: FuelSurchargeSchedule, event: React.MouseEvent) => {
    event.stopPropagation();
    openEditDialog(schedule);
  };

  const openDeleteDialog = (schedule: FuelSurchargeSchedule) => {
    setDeleteDialogState({
      isOpen: true,
      isDeleting: false,
      scheduleToDelete: schedule,
    });
  };

  const closeDeleteDialog = () => {
    setDeleteDialogState((state) => ({ ...state, isOpen: false }));
  };

  const handleDelete = (schedule: FuelSurchargeSchedule, event: React.MouseEvent) => {
    event.stopPropagation();
    openDeleteDialog(schedule);
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialogState.scheduleToDelete) {
      return;
    }

    try {
      setDeleteDialogState((state) => ({ ...state, isDeleting: true }));

      await deleteFuelSurchargeSchedule(deleteDialogState.scheduleToDelete.id);

      setSchedules((prev) =>
        prev.filter((schedule) => schedule.id !== deleteDialogState.scheduleToDelete?.id),
      );

      if (selectedSchedule?.id === deleteDialogState.scheduleToDelete?.id) {
        setSelectedSchedule(undefined);
      }

      toasterStore.push(
        alert(
          t('administration.fuel_surcharge_schedule.deleted_message'),
          AlertTypes.success,
        ),
      );

      closeDeleteDialog();
    } catch (error) {
      console.error('Failed to delete fuel surcharge schedule:', error);
    } finally {
      setDeleteDialogState((state) => ({
        ...state,
        isDeleting: false,
      }));
    }
  };

  const handleDialogSubmit = () => {
    if (dialogState.mode === 'create') {
      handleCreateSchedule();
    } else {
      handleUpdateSchedule();
    }
  };

  return (
    <>
      <Box
        sx={{
          border: `solid 1px ${theme.palette.divider}`,
          display: 'flex',
          // 226px = app bar + SettingsPageHeader + tabs + this panel's top margin, so the
          // panel bottom-aligns to the viewport.
          height: 'calc(100vh - 226px - var(--tread-agent-dock-height, 0px))',
          mb: -4,
          mt: 2,
        }}
      >
        <Box
          sx={{
            borderRight: `solid 1px ${theme.palette.divider}`,
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            height: '100%',
            maxWidth: '240px',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', p: 1.5, flex: 0 }}>
            <SmallButton
              color="brandV2Yellow"
              onClick={openCreateDialog}
              startIcon={<Add />}
            >
              {t('administration.fuel_surcharge_schedule.add_schedule_cta')}
            </SmallButton>
          </Box>

          <Box
            sx={{
              borderTop: `solid 1px ${theme.palette.divider}`,
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              overflow: 'auto',
            }}
          >
            {isLoading ? (
              <LoadingSpinner isVisible sx={{ mt: 2 }} />
            ) : (
              <>
                {schedules.map((schedule) => (
                  <FuelSurchargeScheduleRow
                    key={schedule.id}
                    schedule={schedule}
                    isSelected={selectedSchedule?.id === schedule.id}
                    onSelect={() => setSelectedSchedule(schedule)}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, p: 1.5 }}>
          {isLoading ? (
            <LoadingSpinner isVisible sx={{ mt: 2 }} />
          ) : (
            <Box sx={{ flex: 1 }}>
              {selectedSchedule && (
                <FuelSurchargeSchedulePricesTable
                  fuelSurchargeSchedule={selectedSchedule}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>

      <ConfirmationDialog
        confirmButtonProps={{ children: t('actions.delete'), color: 'error' }}
        isBusy={deleteDialogState.isDeleting}
        isOpen={deleteDialogState.isOpen}
        onClose={() => {
          if (!deleteDialogState.isDeleting) {
            closeDeleteDialog();
          }
        }}
        onConfirm={handleConfirmDelete}
        title={t('administration.fuel_surcharge_schedule.delete_confirm_title')}
      >
        <Typography>
          {t('administration.fuel_surcharge_schedule.delete_confirm_message')}
        </Typography>
      </ConfirmationDialog>

      <Dialog
        isOpen={dialogState.isOpen}
        onClose={() => {
          if (!dialogState.isSaving) {
            closeDialog();
          }
        }}
        preventCloseOnBackdropClick
        sx={{ [`& .${paperClasses.root}`]: { maxWidth: '500px', overflowY: 'visible' } }}
      >
        <DialogHeader
          onClose={() => {
            if (!dialogState.isSaving) {
              closeDialog();
            }
          }}
        >
          {dialogState.mode === 'create'
            ? t('administration.fuel_surcharge_schedule.create_form_title')
            : t('administration.fuel_surcharge_schedule.edit_form_title')}
        </DialogHeader>

        <DialogContent>
          <FuelSurchargeScheduleForm
            ref={formRef}
            initialValues={
              dialogState.mode === 'edit'
                ? {
                    name: dialogState.editingSchedule?.name ?? '',
                    fuelPriceIndex: {
                      id: dialogState.editingSchedule?.fuelPriceIndex.id ?? '',
                      name: dialogState.editingSchedule?.fuelPriceIndex.name ?? '',
                    } as ItemNameAndId,
                    surchargeType:
                      dialogState.editingSchedule?.surchargeType ??
                      FuelSurchargeScheduleType.PERCENTAGE,
                  }
                : undefined
            }
          />
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            disabled={dialogState.isSaving}
            onClick={closeDialog}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            color="brandV2Yellow"
            disabled={dialogState.isSaving}
            loading={dialogState.isSaving}
            onClick={handleDialogSubmit}
          >
            {dialogState.mode === 'create' ? t('actions.create') : t('actions.save')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default FuelSurchargeSchedules;
